import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";

import { apiFetch } from "./client";

/** Support & Exception Board — ticket types + React Query hooks.
 * Thin client over /admin/tickets (see backend app/api/v1/admin/tickets.py).
 *
 * Interaction latency is treated like Linear: every mutation that has an
 * obvious local result (drag to a column, assign, resolve) patches the cache
 * optimistically in onMutate and rolls back on error — the server round-trip
 * reconciles in the background via onSettled invalidation. */

export interface TicketEvent {
  id: string;
  ticket_id: string;
  actor_id: string | null;
  event_type: string;
  from_value: string | null;
  to_value: string | null;
  note: string | null;
  created_at: string;
  // Read-time enrichment (backend get_ticket): human labels for ids
  actor_name?: string | null;
  from_value_label?: string | null;
  to_value_label?: string | null;
}

export interface SupportTicket {
  id: string;
  ticket_number: string;
  pharmacy_order_id: string | null;
  patient_id: string | null;
  warning_key: string | null;
  source: string;
  disposition: string;
  reason_code: string | null;
  priority: number;
  state: string;
  assigned_to: string | null;
  clinician_task_id: string | null;
  title: string;
  subject?: string;
  body: string | null;
  first_flagged_at: string;
  sla_due_at: string | null;
  resolved_at: string | null;
  resolution_note: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  events?: TicketEvent[];
  // Server-side enrichment on list reads (best-effort; null if lookup failed)
  patient_email?: string | null;
  patient_name?: string | null;
  patient_healthie_id?: string | null;
  order_number?: string | null;
  product_id?: string | null;
  product_name?: string | null;
  assigned_to_name?: string | null;
  resolved_by_name?: string | null;
  missing_info_flag_id?: string | null;
  missing_info_reason_code?: string | null;
  missing_info_reason_label?: string | null;
  missing_info_details?: string | null;
  /** Programmatic intake checks the clinician saw when filing the flag. */
  missing_info_findings?: string[] | null;
  /** The patient was sent back to the product intake; the hold clears on re-submission. */
  missing_info_intake_redo?: boolean | null;
  clinician_path?: string | null;
  /** Ops-accessible intake view for the flagged patient. */
  admin_intake_path?: string | null;
}

/** Server-composed subject includes Last, First + treatment when available. */
export function ticketDisplaySubject(ticket: SupportTicket): string {
  return ticket.subject?.trim() || ticket.title;
}

/** An aged intake/Rx ticket the backend handed to ops because its order is
 * now held or approved-but-untransmitted (`metadata.ops_route`). */
const routedToOps = (t: SupportTicket) => Boolean(t.metadata?.ops_route);

/** Ops-owned aged work: routed tickets plus stalled chart filings. */
const heldForOps = (t: SupportTicket) =>
  routedToOps(t) || t.reason_code === "aged:intake_processing";

/** Stage filters aligned with the Overview funnel — client-side predicates
 * over the fetched open set (≤500 rows, so this stays instant). */
export const STAGE_FILTERS: {
  key: string;
  label: string;
  match: (t: SupportTicket) => boolean;
}[] = [
  {
    key: "missing_info",
    label: "Clinician missing info",
    match: (t) => t.source === "missing_info",
  },
  {
    key: "needs_intake",
    label: "Needs intake (stuck)",
    match: (t) => t.reason_code === "aged:awaiting_intake" && !routedToOps(t),
  },
  {
    key: "awaiting_rx",
    label: "Awaiting Rx (stuck)",
    match: (t) => t.reason_code === "aged:awaiting_rx" && !routedToOps(t),
  },
  {
    key: "ops_hold",
    label: "Held for ops",
    match: heldForOps,
  },
  {
    key: "at_pharmacy",
    label: "Awaiting shipment (stuck)",
    match: (t) =>
      t.reason_code === "aged:processing" ||
      t.reason_code === "aged:pending_review",
  },
  {
    key: "warning",
    label: "Pharmacy warning",
    match: (t) => t.source === "warning",
  },
  {
    key: "unhandled",
    label: "Unhandled webhook",
    match: (t) => t.source === "unhandled_webhook",
  },
  { key: "manual", label: "Manual", match: (t) => t.source === "manual" },
];

/** Inclusive date-range match on first_flagged_at (YYYY-MM-DD bounds, local). */
export function ticketInDateRange(
  t: SupportTicket,
  from: string,
  to: string,
): boolean {
  const ts = new Date(t.first_flagged_at).getTime();
  if (Number.isNaN(ts)) return true;
  if (from) {
    const f = new Date(`${from}T00:00:00`).getTime();
    if (!Number.isNaN(f) && ts < f) return false;
  }
  if (to) {
    const u = new Date(`${to}T23:59:59.999`).getTime();
    if (!Number.isNaN(u) && ts > u) return false;
  }
  return true;
}

export function ticketMatchesQuery(t: SupportTicket, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return [
    t.subject,
    t.title,
    t.ticket_number,
    t.patient_email,
    t.patient_name,
    t.product_name,
    t.product_id,
    t.reason_code,
    t.missing_info_reason_label,
    t.missing_info_details,
    t.disposition,
    t.state,
  ]
    .filter(Boolean)
    .some((v) => String(v).toLowerCase().includes(needle));
}

export interface TicketFilters {
  state?: string;
  disposition?: string;
  assignee?: string; // patient id | 'unassigned' | 'me'
  patient_id?: string;
  source?: string;
  overdue?: boolean;
}

export const TICKET_STATES = [
  "new",
  "in_progress",
  "waiting_patient",
  "waiting_clinician",
  "fixed",
] as const;

export const TICKET_STATE_LABELS: Record<string, string> = {
  new: "New",
  in_progress: "In progress",
  waiting_patient: "Waiting on patient",
  waiting_clinician: "Waiting on clinician",
  fixed: "Fixed (awaiting progress)",
  resolved: "Resolved",
};

const LIST_KEY = ["admin", "tickets", "list"] as const;

const jsonInit = (method: string, body?: unknown): RequestInit => ({
  method,
  headers: { "Content-Type": "application/json" },
  ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
});

export function useTickets(filters: TicketFilters = {}) {
  const params = new URLSearchParams();
  if (filters.state) params.set("state", filters.state);
  if (filters.disposition) params.set("disposition", filters.disposition);
  if (filters.assignee) params.set("assignee", filters.assignee);
  if (filters.patient_id) params.set("patient_id", filters.patient_id);
  if (filters.source) params.set("source", filters.source);
  if (filters.overdue) params.set("overdue", "true");
  // Open working set is bounded; fetch the backend max. Beyond 500 open tickets
  // we'd add server-side pagination (the List view renders hundreds cheaply).
  params.set("limit", "500");
  const qs = params.toString();
  return useQuery({
    queryKey: [...LIST_KEY, Object.fromEntries(params.entries())],
    queryFn: () =>
      apiFetch<{ tickets: SupportTicket[] }>(
        `/admin/tickets${qs ? `?${qs}` : ""}`,
      ),
    staleTime: 15_000,
    refetchInterval: 30_000,
    // Filter/tab switches render the previous set instantly while the new
    // request runs — no loading flash (Linear-style).
    // Never carry one patient's rows into another patient's CRM preview.
    placeholderData: filters.patient_id ? undefined : (prev) => prev,
  });
}

export function useTicket(ticketId: string | null) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: ["admin", "tickets", "detail", ticketId],
    queryFn: () => apiFetch<SupportTicket>(`/admin/tickets/${ticketId}`),
    enabled: !!ticketId,
    // Seed from whatever list already holds this ticket so the drawer paints
    // instantly; the fetch fills in `events` when it lands.
    placeholderData: () => {
      if (!ticketId) return undefined;
      for (const [, data] of qc.getQueriesData<{ tickets: SupportTicket[] }>({
        queryKey: LIST_KEY,
      })) {
        const hit = data?.tickets?.find((t) => t.id === ticketId);
        if (hit) return { ...hit, events: [] };
      }
      return undefined;
    },
  });
}

/** Admin roster for the assignee dropdown. */
export function useAdminRoster() {
  return useQuery({
    queryKey: ["admin", "admins"],
    queryFn: () =>
      apiFetch<{
        admins: {
          id: string;
          email: string | null;
          display_name: string | null;
        }[];
      }>("/admin/admins"),
    staleTime: 300_000,
  });
}

/** Patch a ticket across every cached list + its detail entry. */
function patchTicketCaches(
  qc: QueryClient,
  ticketId: string,
  patch: Partial<SupportTicket>,
) {
  qc.setQueriesData<{ tickets: SupportTicket[] }>(
    { queryKey: LIST_KEY },
    (old) =>
      old
        ? {
            ...old,
            tickets: old.tickets.map((t) =>
              t.id === ticketId ? { ...t, ...patch } : t,
            ),
          }
        : old,
  );
  qc.setQueryData<SupportTicket | undefined>(
    ["admin", "tickets", "detail", ticketId],
    (old) => (old ? { ...old, ...patch } : old),
  );
}

/** Snapshot every ticket cache so onError can roll back. */
function snapshotTicketCaches(qc: QueryClient) {
  return qc.getQueriesData<unknown>({ queryKey: ["admin", "tickets"] });
}

function restoreTicketCaches(
  qc: QueryClient,
  snapshots: ReturnType<typeof snapshotTicketCaches>,
) {
  for (const [key, data] of snapshots) qc.setQueryData(key, data);
}

export function useUpdateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      state?: string;
      assigned_to?: string | null;
      unassign?: boolean;
      disposition?: string;
    }) =>
      apiFetch<SupportTicket>(`/admin/tickets/${id}`, jsonInit("PATCH", body)),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ["admin", "tickets"] });
      const snapshots = snapshotTicketCaches(qc);
      const patch: Partial<SupportTicket> = {};
      if (vars.state) patch.state = vars.state;
      if (vars.unassign) patch.assigned_to = null;
      else if (vars.assigned_to !== undefined)
        patch.assigned_to = vars.assigned_to;
      if (vars.disposition) patch.disposition = vars.disposition;
      patchTicketCaches(qc, vars.id, patch);
      return { snapshots };
    },
    onError: (_e, _v, ctx) => ctx && restoreTicketCaches(qc, ctx.snapshots),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "tickets"] }),
  });
}

export function useResolveTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      apiFetch<SupportTicket>(
        `/admin/tickets/${id}/resolve`,
        jsonInit("POST", { note }),
      ),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ["admin", "tickets"] });
      const snapshots = snapshotTicketCaches(qc);
      // Open-ticket lists drop the row immediately; detail marks resolved.
      qc.setQueriesData<{ tickets: SupportTicket[] }>(
        { queryKey: LIST_KEY },
        (old) =>
          old
            ? { ...old, tickets: old.tickets.filter((t) => t.id !== vars.id) }
            : old,
      );
      qc.setQueryData<SupportTicket | undefined>(
        ["admin", "tickets", "detail", vars.id],
        (old) =>
          old
            ? {
                ...old,
                state: "resolved",
                resolved_at: new Date().toISOString(),
              }
            : old,
      );
      return { snapshots };
    },
    onError: (_e, _v, ctx) => ctx && restoreTicketCaches(qc, ctx.snapshots),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "tickets"] }),
  });
}

export function useResolveMissingInfoTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      apiFetch<{
        ticket: SupportTicket;
        resolution: { task_recreated: boolean; task_id: string | null };
      }>(
        `/admin/tickets/${id}/resolve-missing-info`,
        jsonInit("POST", { note }),
      ),
    onSuccess: (_result, vars) => {
      qc.setQueriesData<{ tickets: SupportTicket[] }>(
        { queryKey: LIST_KEY },
        (old) =>
          old
            ? {
                ...old,
                tickets: old.tickets.filter((ticket) => ticket.id !== vars.id),
              }
            : old,
      );
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "tickets"] }),
  });
}

export function useEscalateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      apiFetch<SupportTicket>(
        `/admin/tickets/${id}/escalate-clinician`,
        jsonInit("POST", { note }),
      ),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ["admin", "tickets"] });
      const snapshots = snapshotTicketCaches(qc);
      patchTicketCaches(qc, vars.id, { state: "waiting_clinician" });
      return { snapshots };
    },
    onError: (_e, _v, ctx) => ctx && restoreTicketCaches(qc, ctx.snapshots),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "tickets"] }),
  });
}

export function useAddTicketNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      apiFetch<{ ok: boolean }>(
        `/admin/tickets/${id}/note`,
        jsonInit("POST", { note }),
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "tickets"] }),
  });
}

export interface CreateTicketInput {
  title: string;
  body?: string;
  disposition?: string;
  pharmacy_order_id?: string;
  patient_id?: string;
}

export function useCreateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTicketInput) =>
      apiFetch<SupportTicket>("/admin/tickets", jsonInit("POST", input)),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "tickets"] }),
  });
}

export interface OverviewRow {
  order_id: string;
  order_number: string | null;
  product_id: string | null;
  patient_id: string | null;
  partner_key: string | null;
  status: string;
  age_days: number | null;
  ticket_id: string | null;
  ticket_state: string | null;
  ticket_source?: string | null;
  ticket_assigned_to: string | null;
  patient_email?: string | null;
  patient_name?: string | null;
  pending_review?: boolean;
  /** Derived awaiting_prescription sub-state (patient_blocked / ops_hold rows). */
  derived_status?: string;
  reason_code?: string;
  reason_description?: string | null;
}

export interface SupportOverview {
  date_from: string;
  date_through: string;
  metrics: {
    needs_intake: number;
    awaiting_consult: number;
    /** Absent until the backend derived-status change deploys. */
    patient_blocked?: number;
    awaiting_rx: number;
    ops_hold?: number;
    at_pharmacy: number;
    open_warnings: number;
    overdue_tickets: number;
    shipped_last_7d: number;
  };
  stages: {
    needs_intake: OverviewRow[];
    awaiting_consult: OverviewRow[];
    patient_blocked?: OverviewRow[];
    awaiting_rx: OverviewRow[];
    ops_hold?: OverviewRow[];
    at_pharmacy: OverviewRow[];
    warnings: OverviewRow[];
  };
}

export function useSupportOverview(
  dateFrom: string,
  dateThrough: string,
  enabled: boolean,
) {
  const params = new URLSearchParams({
    date_from: dateFrom,
    date_through: dateThrough,
  });
  return useQuery({
    queryKey: ["admin", "tickets", "overview", dateFrom, dateThrough],
    queryFn: () =>
      apiFetch<SupportOverview>(`/admin/tickets/overview?${params}`),
    enabled,
    staleTime: 600_000,
    refetchOnWindowFocus: false,
  });
}

// ── Saved views ──────────────────────────────────────────────────────────────
export interface SavedView {
  id: string;
  name: string;
  owner_id: string;
  owner_name?: string | null;
  shared: boolean;
  filters: Record<string, string>;
  view_mode: "kanban" | "list";
  created_at: string;
}

export function useSupportViews() {
  return useQuery({
    queryKey: ["admin", "tickets", "views"],
    queryFn: () =>
      apiFetch<{ views: SavedView[]; viewer_id: string | null }>(
        "/admin/tickets/views",
      ),
    staleTime: 30_000,
  });
}

export function useCreateView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      shared: boolean;
      filters: Record<string, string>;
      view_mode: string;
    }) => apiFetch<SavedView>("/admin/tickets/views", jsonInit("POST", input)),
    onSettled: () =>
      qc.invalidateQueries({ queryKey: ["admin", "tickets", "views"] }),
  });
}

export function useDeleteView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ deleted: boolean }>(
        `/admin/tickets/views/${id}`,
        jsonInit("DELETE"),
      ),
    onSettled: () =>
      qc.invalidateQueries({ queryKey: ["admin", "tickets", "views"] }),
  });
}

// ── Auto-assignment settings ─────────────────────────────────────────────────
export interface AutoAssignRule {
  id?: string; // stable key for round-robin cursor state
  name: string;
  dispositions: string[]; // ['all'] or specific dispositions
  pool: string[]; // patient ids
  strategy: "round_robin" | "least_loaded";
  enabled: boolean;
}

export function useSupportSettings() {
  return useQuery({
    queryKey: ["admin", "tickets", "settings"],
    queryFn: () =>
      apiFetch<{ auto_assign_rules: AutoAssignRule[] }>(
        "/admin/tickets/settings",
      ),
    staleTime: 30_000,
  });
}

export function useSaveSupportSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rules: AutoAssignRule[]) =>
      apiFetch<{ auto_assign_rules: AutoAssignRule[] }>(
        "/admin/tickets/settings",
        jsonInit("PUT", { auto_assign_rules: rules }),
      ),
    onSettled: () =>
      qc.invalidateQueries({ queryKey: ["admin", "tickets", "settings"] }),
  });
}

export function useApplyAssignRules() {
  const qc = useQueryClient();
  return useMutation({
    // Batched: callers loop until remaining === 0 (or assigned === 0, meaning
    // no enabled rule matches what's left), rendering progress between calls.
    mutationFn: (limit: number = 25) =>
      apiFetch<{ assigned: number; remaining: number }>(
        `/admin/tickets/settings/apply-unassigned?limit=${limit}`,
        jsonInit("POST"),
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "tickets"] }),
  });
}

// ── Metrics ──────────────────────────────────────────────────────────────────
export interface TicketMetrics {
  open_total: number;
  assigned: number;
  unassigned: number;
  overdue: number;
  by_stage: Record<string, number>;
  by_assignee: { id: string; name: string; count: number }[];
  resolved_last_24h: number;
  resolved_last_7d: number;
}

export function useTicketMetrics() {
  return useQuery({
    queryKey: ["admin", "tickets", "metrics"],
    queryFn: () => apiFetch<TicketMetrics>("/admin/tickets/metrics"),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useRunTicketSweep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ ok: boolean; counts: Record<string, number> }>(
        "/admin/tickets/sweep",
        jsonInit("POST"),
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "tickets"] }),
  });
}
